/**
 * 공정 흐름도 — 물질·에너지수지 네트워크.
 *
 * ⚠ **Aspen 같은 진짜 공정모사가 아니다.** 조성이 고정된, 상변화 없는
 *   스트림의 물질·에너지수지만 다룬다. 유일한 예외가 **물(수증기)의
 *   응축·증발**이다 — 스크러버 노드에서 가스가 그 온도·압력의 포화
 *   상태로 나온다고 보고 물만 추적한다(다성분 기액평형이 아니다).
 *   다른 성분이 같이 응축하는 경우(탄화수소 이슬점 등)는 범위 밖이다.
 *
 * ⚠ 순환(리사이클)은 다루지 않는다 — 소스에서 싱크로 가는
 *   유향비순환그래프(DAG)만 허용한다.
 *
 * 재사용:
 *   · 기체 배관 구간 — `isothermalFlow.ts` 의 `solveOutletPressure()`
 *     (API 521 5.5.5 등온 압축성 식을 상류→하류 방향으로 푼 것) 와
 *     `mixStreams()` (KOSHA D-63 식 5-1~5-4 합류 물성).
 *   · 액체 배관 구간 — `pipeFlow.ts` 의 `pressureDrop()`
 *     (Darcy–Weisbach + Colebrook–White, 표준식 아님 — 배관 검토 탭과 동일).
 *   · 물의 포화증기압 — `waterVaporPressure.ts` (Antoine 식).
 */
import {
  frictionFactor,
  pressureDrop,
  reynolds,
  type LossMethod,
} from "./pipeFlow";
import { buildSegment } from "./pipeFlow";
import {
  mixStreams,
  solveOutletPressure,
  type StreamProperties,
} from "./isothermalFlow";
import { waterSaturationPressureKpa } from "./waterVaporPressure";
import { mixComponentProperties } from "./gasComposition";
import type { GasComponent } from "../data/gasComponents";
import {
  reduceIronOreWithHydrogen,
  esfOffgasFromCarbon,
  type ReductionSuccess,
  type EsfOffgasSuccess,
} from "./ironReduction";

/** 물의 분자량 (kg/kmol). */
export const WATER_MOLECULAR_WEIGHT = 18.015;
/** 수증기의 정압비열 근사값 (J/kg·K) — 대기압 부근, 100~300 ℃ 범위. */
export const WATER_VAPOR_SPECIFIC_HEAT_JKGK = 1996;
/** 물의 밀도 근사값 (kg/m³) — 상온 부근. 응축수·보충수 체적 환산용. */
export const WATER_LIQUID_DENSITY_KGM3 = 1000;

/* ── 스트림 ──────────────────────────────────────────────── */

export interface WetGasStream {
  readonly phase: "gas";
  /**
   * 건가스 성분별 질량유량 (kg/h). 키는 `gasComponents.ts` 의 성분 key.
   * 분자량·비열비·비열·점도는 여기서 그때그때 유도한다
   * (`gasComposition.ts` `mixComponentProperties()`) — 스트림에 값을
   * 따로 들고 다니지 않는다. 반응 노드(환원로 등)가 특정 성분(H2 등)의
   * 양을 알아야 하므로 성분 단위로 끝까지 들고 다닌다.
   */
  readonly components: Readonly<Record<string, number>>;
  /**
   * `components` 에 표준 데이터베이스(`gasComponents.ts`)에 없는 key가
   * 있을 때 그 key를 풀이할 가상 성분 정의("평균값 직접 입력" 소스 노드가
   * 자기 자신을 성분 하나처럼 표현하는 용도 — `flowsheetState.ts` 참고).
   */
  readonly customComponentDefs?: Readonly<Record<string, GasComponent>>;
  readonly compressibility: number;
  /** 수증기 질량유량 (kg/h). 건조 가스면 0. */
  readonly waterVaporMassFlowKgH: number;
  readonly temperatureK: number;
  readonly pressureKpaAbs: number;
}

export interface LiquidStream {
  readonly phase: "liquid";
  readonly massFlowKgH: number;
  readonly densityKgM3: number;
  readonly viscosityCp: number;
  readonly specificHeatJKgK: number;
  readonly temperatureK: number;
  readonly pressureKpaAbs: number;
}

export type ProcessStream = WetGasStream | LiquidStream;

/* ── 노드 ────────────────────────────────────────────────── */

export interface SourceNode {
  readonly kind: "source";
  readonly id: string;
  readonly label: string;
  readonly stream: ProcessStream;
}

export interface SinkNode {
  readonly kind: "sink";
  readonly id: string;
  readonly label: string;
  readonly inputId: string;
}

export interface MixerNode {
  readonly kind: "mixer";
  readonly id: string;
  readonly label: string;
  readonly inputIds: readonly string[];
}

export interface SplitterBranch {
  readonly id: string;
  readonly label: string;
  /** true 면 fraction(0~1), false 면 고정 유량(kg/h)으로 본다. */
  readonly isFraction: boolean;
  readonly value: number;
}

export interface SplitterNode {
  readonly kind: "splitter";
  readonly id: string;
  readonly label: string;
  readonly inputId: string;
  readonly branches: readonly SplitterBranch[];
}

export interface PipeNode {
  readonly kind: "pipe";
  readonly id: string;
  readonly label: string;
  readonly inputId: string;
  readonly innerDiameterM: number;
  /** 직관 + 부속 등가길이 합 (m) */
  readonly equivalentLengthM: number;
  readonly roughnessM: number;
  /** 손실계수 합 ΣK — 등가길이로 못 바꾼 몫(입구·출구 등). 기체 구간은 쓰지 않는다. */
  readonly fittingK: number;
}

export interface HeaterNode {
  readonly kind: "heater";
  readonly id: string;
  readonly label: string;
  readonly inputId: string;
  /** 전열량 (W). 양수면 가열, 음수면 냉각. */
  readonly dutyW: number;
}

export interface ScrubberNode {
  readonly kind: "scrubber";
  readonly id: string;
  readonly label: string;
  readonly inputId: string;
  readonly outletTemperatureK: number;
  readonly outletPressureKpaAbs: number;
  /** 포화도. 기본 1.0(완전 포화). */
  readonly saturationFraction?: number;
  /** 증발 방향일 때 공급 가능한 보충수 (kg/h). 없으면 0. */
  readonly makeupWaterMassFlowKgH?: number;
  /** 이 노드가 만드는 두 출력의 참조 id */
  readonly gasOutputId: string;
  readonly liquidOutputId: string;
}

export interface ValveNode {
  readonly kind: "valve";
  readonly id: string;
  readonly label: string;
  readonly inputId: string;
  readonly pressureDropKpa: number;
}

export interface ReductionFurnaceNode {
  readonly kind: "reductionFurnace";
  readonly id: string;
  readonly label: string;
  readonly inputId: string;
  /** 광석 중 Fe2O3 질량유량 (kg/h). */
  readonly oreFe2O3MassFlowKgH: number;
  /** 목표 금속화율 (%). */
  readonly targetMetallizationPercent: number;
}

export interface EsfOffgasNode {
  readonly kind: "esfOffgas";
  readonly id: string;
  readonly label: string;
  /** 유동로와 이어지지 않는 독립 노드 — 입력이 없다. */
  readonly oreFe2O3MassFlowKgH: number;
  readonly metallizationPercent: number;
  readonly carbonMassFlowKgH: number;
  readonly temperatureK: number;
  readonly pressureKpaAbs: number;
}

export type ProcessNode =
  | SourceNode
  | SinkNode
  | MixerNode
  | SplitterNode
  | PipeNode
  | HeaterNode
  | ScrubberNode
  | ValveNode
  | ReductionFurnaceNode
  | EsfOffgasNode;

/* ── 결과 ────────────────────────────────────────────────── */

export interface ProcessNetworkFailure {
  readonly ok: false;
  readonly reason:
    | "invalid-input"
    | "invalid-topology"
    | "calc-failed"
    | "choked"
    | "no-solution";
  readonly message: string;
  readonly where?: string;
}

export interface ProcessNetworkSuccess {
  readonly ok: true;
  /** 계산 순서(위상 정렬) 그대로 — 표에 이 순서로 싣는다. */
  readonly nodes: readonly ProcessNode[];
  /** 출력 지점 id → 그 지점의 스트림 */
  readonly streams: ReadonlyMap<string, ProcessStream>;
  readonly warnings: readonly string[];
  /**
   * 환원로/ESF 같은 반응 노드가 만드는 요약값(id → 요약) — 물질·에너지
   * 수지표(스트림)에는 안 나가는 값(H2 소모량·이용률·도달 금속화율 등)을
   * 화면 노드 카드에 바로 보여줄 때 쓴다.
   */
  readonly reactionSummaries: ReadonlyMap<string, ReductionSuccess | EsfOffgasSuccess>;
}

export type ProcessNetworkResult =
  | ProcessNetworkSuccess
  | ProcessNetworkFailure;

function fail(
  reason: ProcessNetworkFailure["reason"],
  message: string,
  where?: string,
): ProcessNetworkFailure {
  return where === undefined
    ? { ok: false, reason, message }
    : { ok: false, reason, message, where };
}

/* ── 위상 정렬 ────────────────────────────────────────────── */

/** 노드가 만드는 출력 지점 id 목록. 대부분 노드 자신의 id 하나뿐이다. */
function outputPointsOf(n: ProcessNode): readonly string[] {
  switch (n.kind) {
    case "sink":
      return [];
    case "splitter":
      return n.branches.map((b) => b.id);
    case "scrubber":
      return [n.gasOutputId, n.liquidOutputId];
    default:
      return [n.id];
  }
}

/** 노드가 참조하는(의존하는) 입력 지점 id 목록. */
function inputPointsOf(n: ProcessNode): readonly string[] {
  switch (n.kind) {
    case "source":
    case "esfOffgas":
      return [];
    case "mixer":
      return n.inputIds;
    default:
      return [n.inputId];
  }
}

interface TopologyOrderSuccess {
  readonly ok: true;
  /** source → sink 방향으로 계산할 수 있는 순서. */
  readonly order: readonly ProcessNode[];
}
type TopologyOrder = TopologyOrderSuccess | ProcessNetworkFailure;

function buildTopology(
  nodes: readonly ProcessNode[],
): TopologyOrder | ProcessNetworkFailure {
  if (nodes.length === 0) {
    return fail("invalid-input", "노드가 하나도 없습니다.");
  }

  const byId = new Map<string, ProcessNode>();
  const producerOf = new Map<string, string>(); // outputPointId -> nodeId
  for (const n of nodes) {
    if (byId.has(n.id)) {
      return fail("invalid-topology", `노드 id 가 중복됩니다: ${n.label}`, n.label);
    }
    byId.set(n.id, n);
    for (const p of outputPointsOf(n)) {
      if (producerOf.has(p)) {
        return fail(
          "invalid-topology",
          `출력 지점 id 가 중복됩니다: ${p} (${n.label})`,
          n.label,
        );
      }
      producerOf.set(p, n.id);
    }
  }

  // 각 노드가 의존하는 상류 노드 id 집합.
  const dependsOn = new Map<string, Set<string>>();
  for (const n of nodes) {
    const deps = new Set<string>();
    for (const p of inputPointsOf(n)) {
      const producerId = producerOf.get(p);
      if (producerId === undefined) {
        return fail(
          "invalid-topology",
          `'${n.label}' 이 없는 지점('${p}')을 참조합니다.`,
          n.label,
        );
      }
      deps.add(producerId);
    }
    dependsOn.set(n.id, deps);
  }

  // Kahn 알고리즘.
  const remaining = new Map(dependsOn);
  const order: ProcessNode[] = [];
  const ready: string[] = nodes
    .filter((n) => remaining.get(n.id)!.size === 0)
    .map((n) => n.id);

  if (ready.length === 0) {
    return fail(
      "invalid-topology",
      "시작할 수 있는 노드(소스)가 없습니다 — 순환 구조이거나 모든 노드가 " +
        "서로를 참조하고 있습니다.",
    );
  }
  void 0;

  const doneIds = new Set<string>();
  while (ready.length > 0) {
    const id = ready.shift()!;
    const n = byId.get(id)!;
    order.push(n);
    doneIds.add(id);
    for (const other of nodes) {
      const deps = remaining.get(other.id)!;
      if (deps.has(id)) {
        deps.delete(id);
        if (deps.size === 0 && !doneIds.has(other.id)) {
          ready.push(other.id);
        }
      }
    }
  }

  if (order.length !== nodes.length) {
    const stranded = nodes.filter((n) => !doneIds.has(n.id));
    return fail(
      "invalid-topology",
      `계산 순서를 정할 수 없는 노드가 있습니다(순환 구조로 보입니다): ${stranded
        .map((n) => n.label)
        .join(", ")}.`,
      stranded[0]?.label,
    );
  }

  return { ok: true, order };
}

/* ── 노드별 계산 ─────────────────────────────────────────── */

function invalid(message: string, where?: string): ProcessNetworkFailure {
  return fail("invalid-input", message, where);
}

function asGas(s: ProcessStream, where: string): WetGasStream | ProcessNetworkFailure {
  if (s.phase !== "gas") return invalid(`'${where}' 은(는) 기체 스트림이 아닙니다.`, where);
  return s;
}
function asLiquid(s: ProcessStream, where: string): LiquidStream | ProcessNetworkFailure {
  if (s.phase !== "liquid") return invalid(`'${where}' 은(는) 액체 스트림이 아닙니다.`, where);
  return s;
}

/**
 * 건가스 성분을 먼저 하나로 뭉치고(`mixComponentProperties`), 그걸
 * mixStreams() 로 섞을 수 있는 형태로 바꾼다(건가스 뭉치 + 수증기 2성분).
 */
function wetGasAsMixInputs(s: WetGasStream): StreamProperties[] {
  const parts: StreamProperties[] = [];
  const dry = mixComponentProperties(s.components, s.customComponentDefs);
  if (dry !== null) {
    parts.push({
      massFlowKgH: dry.totalMassFlowKgH,
      molecularWeight: dry.molecularWeight,
      temperatureK: s.temperatureK,
      compressibility: s.compressibility,
      viscosityCp: dry.viscosityCp,
    });
  }
  if (s.waterVaporMassFlowKgH > 0) {
    parts.push({
      massFlowKgH: s.waterVaporMassFlowKgH,
      molecularWeight: WATER_MOLECULAR_WEIGHT,
      temperatureK: s.temperatureK,
      compressibility: 1,
      viscosityCp: 0.012, // 수증기 통상값 — 별도 입력을 받지 않는다
    });
  }
  return parts;
}

function heatCapacityFlowWK(s: ProcessStream): number {
  if (s.phase === "liquid") {
    return (s.massFlowKgH / 3600) * s.specificHeatJKgK;
  }
  const dry = mixComponentProperties(s.components, s.customComponentDefs);
  return (
    (dry === null ? 0 : (dry.totalMassFlowKgH / 3600) * dry.specificHeatJKgK) +
    (s.waterVaporMassFlowKgH / 3600) * WATER_VAPOR_SPECIFIC_HEAT_JKGK
  );
}

export function solveProcessNetwork(
  nodes: readonly ProcessNode[],
): ProcessNetworkResult {
  const topo = buildTopology(nodes);
  if (!topo.ok) return topo;
  const { order } = topo;

  const streams = new Map<string, ProcessStream>();
  const reactionSummaries = new Map<string, ReductionSuccess | EsfOffgasSuccess>();
  const warnings: string[] = [];
  const pushWarning = (w: string) => {
    if (!warnings.includes(w)) warnings.push(w);
  };

  for (const n of order) {
    switch (n.kind) {
      case "source": {
        streams.set(n.id, n.stream);
        break;
      }

      case "sink": {
        // 출력이 없다 — 입력 스트림이 이미 streams 에 있으니 그대로 둔다.
        if (!streams.has(n.inputId)) {
          return invalid(`'${n.label}' 의 입력을 찾을 수 없습니다.`, n.label);
        }
        break;
      }

      case "mixer": {
        const inputs = n.inputIds.map((id) => streams.get(id)!);
        if (inputs.length === 0) {
          return invalid(`'${n.label}' 에 입력이 없습니다.`, n.label);
        }
        const phase = inputs[0]!.phase;
        if (inputs.some((s) => s.phase !== phase)) {
          return invalid(
            `'${n.label}' 에서 기체와 액체 스트림을 함께 섞을 수 없습니다 ` +
              "(상분리기가 아닌 단순 합류 노드입니다).",
            n.label,
          );
        }

        if (phase === "gas") {
          const gasInputs = inputs as WetGasStream[];
          // T(질량가중)·Z(몰가중)는 그대로 mixStreams() 로 구한다 — 성분별
          // 합산과는 무관한, 이미 정확한 로직이다.
          const mixed = mixStreams(gasInputs.flatMap(wetGasAsMixInputs));
          if (mixed === null) {
            return invalid(`'${n.label}' 에서 섞을 유량이 없습니다.`, n.label);
          }
          const totalWater = gasInputs.reduce(
            (a, s) => a + s.waterVaporMassFlowKgH,
            0,
          );
          // 성분별로 그대로 더한다 — 진짜 조성 혼합이라 평균값을 다시
          // 평균 내는 것보다 정확하다. 분자량·비열비·비열·점도는 이
          // 합쳐진 성분에서 나중에 그때그때 유도한다(위 wetGasAsMixInputs 등).
          const components: Record<string, number> = {};
          const customComponentDefs: Record<string, GasComponent> = {};
          for (const s of gasInputs) {
            for (const [key, flow] of Object.entries(s.components)) {
              components[key] = (components[key] ?? 0) + flow;
            }
            if (s.customComponentDefs) Object.assign(customComponentDefs, s.customComponentDefs);
          }
          const pressures = gasInputs.map((s) => s.pressureKpaAbs);
          const pOut = Math.min(...pressures);
          if (Math.max(...pressures) - pOut > 0.01 * pOut) {
            pushWarning(
              `'${n.label}' 로 합류하는 스트림들의 압력이 서로 달라(최대 ` +
                `${Math.max(...pressures).toFixed(1)} vs 최소 ${pOut.toFixed(1)} kPa) ` +
                "낮은 쪽으로 맞췄습니다 — 실제로는 역류·조리개 등 별도 검토가 필요할 수 있습니다.",
            );
          }
          for (const w of mixed.warnings) pushWarning(w);
          streams.set(n.id, {
            phase: "gas",
            components,
            ...(Object.keys(customComponentDefs).length > 0 ? { customComponentDefs } : {}),
            compressibility: mixed.compressibility,
            waterVaporMassFlowKgH: totalWater,
            temperatureK: mixed.temperatureK,
            pressureKpaAbs: pOut,
          });
        } else {
          const liqInputs = inputs as LiquidStream[];
          const totalMass = liqInputs.reduce((a, s) => a + s.massFlowKgH, 0);
          if (totalMass <= 0) {
            return invalid(`'${n.label}' 에서 섞을 유량이 없습니다.`, n.label);
          }
          const wAvg = (f: (s: LiquidStream) => number) =>
            liqInputs.reduce((a, s) => a + s.massFlowKgH * f(s), 0) / totalMass;
          const pOut = Math.min(...liqInputs.map((s) => s.pressureKpaAbs));
          streams.set(n.id, {
            phase: "liquid",
            massFlowKgH: totalMass,
            densityKgM3: wAvg((s) => s.densityKgM3),
            viscosityCp: wAvg((s) => s.viscosityCp),
            specificHeatJKgK: wAvg((s) => s.specificHeatJKgK),
            temperatureK: wAvg((s) => s.temperatureK),
            pressureKpaAbs: pOut,
          });
        }
        break;
      }

      case "splitter": {
        const input = streams.get(n.inputId)!;
        const totalFlow =
          input.phase === "gas"
            ? Object.values(input.components).reduce((a, b) => a + b, 0) +
              input.waterVaporMassFlowKgH
            : input.massFlowKgH;

        const fixedSum = n.branches
          .filter((b) => !b.isFraction)
          .reduce((a, b) => a + b.value, 0);
        const fracSum = n.branches
          .filter((b) => b.isFraction)
          .reduce((a, b) => a + b.value, 0);
        if (fracSum > 1 + 1e-9) {
          return invalid(
            `'${n.label}' 의 분율 합이 1을 넘습니다(${fracSum.toFixed(3)}).`,
            n.label,
          );
        }
        if (fixedSum > totalFlow + 1e-6) {
          return invalid(
            `'${n.label}' 의 고정유량 합(${fixedSum.toFixed(1)} kg/h)이 ` +
              `입구 유량(${totalFlow.toFixed(1)} kg/h)보다 큽니다.`,
            n.label,
          );
        }

        for (const b of n.branches) {
          const branchFlow = b.isFraction ? totalFlow * b.value : b.value;
          const ratio = totalFlow > 0 ? branchFlow / totalFlow : 0;
          if (input.phase === "gas") {
            const scaledComponents = Object.fromEntries(
              Object.entries(input.components).map(([k, v]) => [k, v * ratio]),
            );
            streams.set(b.id, {
              ...input,
              components: scaledComponents,
              waterVaporMassFlowKgH: input.waterVaporMassFlowKgH * ratio,
            });
          } else {
            streams.set(b.id, {
              ...input,
              massFlowKgH: input.massFlowKgH * ratio,
            });
          }
        }
        break;
      }

      case "pipe": {
        const input = streams.get(n.inputId)!;
        if (input.phase === "gas") {
          const mixed = mixStreams(wetGasAsMixInputs(input));
          if (mixed === null) {
            return invalid(`'${n.label}' 에 흐르는 유량이 없습니다.`, n.label);
          }
          const re = reynolds(
            mixed.massFlowKgH / 3600,
            n.innerDiameterM,
            mixed.viscosityCp / 1000,
          );
          const f = frictionFactor(re, n.roughnessM / n.innerDiameterM);
          const solved = solveOutletPressure({
            massFlowKgH: mixed.massFlowKgH,
            inletPressureKpaAbs: input.pressureKpaAbs,
            innerDiameterM: n.innerDiameterM,
            equivalentLengthM: n.equivalentLengthM,
            frictionFactor: f,
            compressibility: mixed.compressibility,
            temperatureK: mixed.temperatureK,
            molecularWeight: mixed.molecularWeight,
          });
          if (!solved.ok) {
            return fail(solved.reason, `'${n.label}': ${solved.message}`, n.label);
          }
          streams.set(n.id, {
            ...input,
            pressureKpaAbs: solved.outletPressureKpaAbs,
          });
        } else {
          const seg = buildSegment(
            0,
            n.innerDiameterM,
            n.roughnessM,
            {
              equivalentLengthM: n.equivalentLengthM,
              headOnlyK: n.fittingK,
              totalKEquivalent: n.fittingK,
            },
            "equivalent-length" as LossMethod,
          );
          const drop = pressureDrop(
            seg,
            input.massFlowKgH / 3600,
            input.densityKgM3,
            input.viscosityCp / 1000,
          );
          const pOut = input.pressureKpaAbs - drop.pressureDropPa / 1000;
          if (pOut <= 0) {
            return fail(
              "invalid-input",
              `'${n.label}' 의 압력손실이 입구압보다 커서 출구압이 0 이하가 됩니다.`,
              n.label,
            );
          }
          streams.set(n.id, { ...input, pressureKpaAbs: pOut });
        }
        break;
      }

      case "heater": {
        const input = streams.get(n.inputId)!;
        const cpFlow = heatCapacityFlowWK(input);
        if (cpFlow <= 0) {
          return invalid(`'${n.label}' 에 흐르는 유량이 없습니다.`, n.label);
        }
        const dT = n.dutyW / cpFlow;
        const tOut = input.temperatureK + dT;
        if (input.phase === "gas") {
          if (input.waterVaporMassFlowKgH > 0 && dT < 0) {
            const psat = waterSaturationPressureKpa(tOut);
            const dry = mixComponentProperties(input.components, input.customComponentDefs);
            const nDryMol =
              dry === null ? 0 : dry.totalMassFlowKgH / dry.molecularWeight;
            const nWaterMol = input.waterVaporMassFlowKgH / WATER_MOLECULAR_WEIGHT;
            const yWater = nWaterMol / (nWaterMol + nDryMol);
            if (yWater * input.pressureKpaAbs > psat.kpaAbs) {
              pushWarning(
                `'${n.label}' 는 감열(냉각)만 계산합니다 — 이 온도까지 ` +
                  "냉각하면 실제로는 가스 속 수분이 응축될 것으로 보이는데 " +
                  "이 노드는 그걸 반영하지 않습니다. 응축을 봐야 하면 " +
                  "히터 대신 스크러버 노드를 쓰십시오.",
              );
            }
          }
          streams.set(n.id, { ...input, temperatureK: tOut });
        } else {
          streams.set(n.id, { ...input, temperatureK: tOut });
        }
        break;
      }

      case "scrubber": {
        const gasR = asGas(streams.get(n.inputId)!, n.label);
        if ("ok" in gasR) return gasR;
        const gas = gasR;

        const sat = n.saturationFraction ?? 1.0;
        const psat = waterSaturationPressureKpa(n.outletTemperatureK);
        if (!Number.isFinite(psat.kpaAbs)) {
          return invalid(`'${n.label}' 의 출구 온도가 올바르지 않습니다.`, n.label);
        }
        if (psat.extrapolated) {
          pushWarning(
            `'${n.label}' 의 출구 온도가 물 포화증기압 상관식의 유효범위를 ` +
              "크게 벗어나 외삽했습니다 — 결과를 정식 스팀표로 재확인하십시오.",
          );
        }
        const pOut = n.outletPressureKpaAbs;
        const pWaterOut = sat * psat.kpaAbs;
        if (pWaterOut >= pOut) {
          return fail(
            "invalid-input",
            `'${n.label}' — 출구 온도에서의 포화증기압(${psat.kpaAbs.toFixed(1)} kPa)이 ` +
              `출구 압력(${pOut.toFixed(1)} kPa) 이상입니다. 이 조건에서는 가스만으로는 ` +
              "포화시킬 수 없습니다(전량 증기가 되어버립니다) — 압력을 높이거나 온도를 낮추십시오.",
            n.label,
          );
        }

        const dry = mixComponentProperties(gas.components, gas.customComponentDefs);
        if (dry === null) {
          return invalid(`'${n.label}' 입구에 건가스 성분이 없습니다.`, n.label);
        }
        const nDry = dry.totalMassFlowKgH / dry.molecularWeight;
        // y = pWater/P = nWater/(nWater+nDry)  →  nWater = y·nDry/(1−y)
        const yWater = pWaterOut / pOut;
        const nWaterOut = (yWater * nDry) / (1 - yWater);
        const waterOutKgH = nWaterOut * WATER_MOLECULAR_WEIGHT;

        const deltaKgH = gas.waterVaporMassFlowKgH - waterOutKgH; // 양수=응축, 음수=증발
        let liquidOut: LiquidStream;
        if (deltaKgH >= 0) {
          liquidOut = {
            phase: "liquid",
            massFlowKgH: deltaKgH,
            densityKgM3: WATER_LIQUID_DENSITY_KGM3,
            viscosityCp: 1.0,
            specificHeatJKgK: 4186,
            temperatureK: n.outletTemperatureK,
            pressureKpaAbs: pOut,
          };
        } else {
          const needed = -deltaKgH;
          const available = n.makeupWaterMassFlowKgH ?? 0;
          if (available < needed) {
            pushWarning(
              `'${n.label}' 에서 가스가 포화되려면 물 ${needed.toFixed(1)} kg/h 가 ` +
                `더 증발해야 하는데, 지정한 보충수(${available.toFixed(1)} kg/h)로는 ` +
                "부족합니다 — 가스가 목표만큼 포화되지 못했을 수 있습니다.",
            );
          }
          liquidOut = {
            phase: "liquid",
            massFlowKgH: Math.max(available - needed, 0),
            densityKgM3: WATER_LIQUID_DENSITY_KGM3,
            viscosityCp: 1.0,
            specificHeatJKgK: 4186,
            temperatureK: n.outletTemperatureK,
            pressureKpaAbs: pOut,
          };
        }

        streams.set(n.gasOutputId, {
          phase: "gas",
          components: gas.components,
          ...(gas.customComponentDefs ? { customComponentDefs: gas.customComponentDefs } : {}),
          compressibility: gas.compressibility,
          waterVaporMassFlowKgH: waterOutKgH,
          temperatureK: n.outletTemperatureK,
          pressureKpaAbs: pOut,
        });
        streams.set(n.liquidOutputId, liquidOut);
        break;
      }

      case "valve": {
        const input = streams.get(n.inputId)!;
        const pOut =
          (input.phase === "gas" ? input.pressureKpaAbs : input.pressureKpaAbs) -
          n.pressureDropKpa;
        if (pOut <= 0) {
          return invalid(
            `'${n.label}' 의 압력강하가 입구압보다 커서 출구압이 0 이하가 됩니다.`,
            n.label,
          );
        }
        streams.set(n.id, { ...input, pressureKpaAbs: pOut });
        break;
      }

      case "reductionFurnace": {
        const gasR = asGas(streams.get(n.inputId)!, n.label);
        if ("ok" in gasR) return gasR;
        const gas = gasR;

        const result = reduceIronOreWithHydrogen({
          oreFe2O3MassFlowKgH: n.oreFe2O3MassFlowKgH,
          targetMetallizationPercent: n.targetMetallizationPercent,
          inletGasComponents: gas.components,
        });
        if (!result.ok) {
          return invalid(`'${n.label}' — ${result.message}`, n.label);
        }
        if (result.warning) pushWarning(`'${n.label}' — ${result.warning}`);
        reactionSummaries.set(n.id, result);

        streams.set(n.id, {
          phase: "gas",
          components: result.outletGasComponents,
          ...(gas.customComponentDefs ? { customComponentDefs: gas.customComponentDefs } : {}),
          compressibility: gas.compressibility,
          waterVaporMassFlowKgH: gas.waterVaporMassFlowKgH + result.producedH2OMassFlowKgH,
          temperatureK: gas.temperatureK,
          pressureKpaAbs: gas.pressureKpaAbs,
        });
        break;
      }

      case "esfOffgas": {
        const result = esfOffgasFromCarbon({
          oreFe2O3MassFlowKgH: n.oreFe2O3MassFlowKgH,
          metallizationPercent: n.metallizationPercent,
          carbonMassFlowKgH: n.carbonMassFlowKgH,
        });
        if (!result.ok) {
          return invalid(`'${n.label}' — ${result.message}`, n.label);
        }
        if (result.warning) pushWarning(`'${n.label}' — ${result.warning}`);
        reactionSummaries.set(n.id, result);

        streams.set(n.id, {
          phase: "gas",
          components: { "carbon-monoxide": result.coMassFlowKgH },
          compressibility: 1,
          waterVaporMassFlowKgH: 0,
          temperatureK: n.temperatureK,
          pressureKpaAbs: n.pressureKpaAbs,
        });
        break;
      }
    }
  }

  return { ok: true, nodes: order, streams, warnings, reactionSummaries };
}
